import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Collapse,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  AccessTime as TimeIcon,
  CheckCircle as CompleteIcon,
  Edit as EditIcon,
  PlayArrow as StartIcon,
  Done as DoneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Replay as ReopenIcon,
  PersonAdd as AssignIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import api, { timeEntriesAPI, tasksAPI, projectsAPI, usersAPI, customersAPI, dashboardAPI } from '../services/api';
import TaskEditDialog from '../components/TaskEditDialog';
import TaskCreateDialog from '../components/TaskCreateDialog';
import QuickTimeEntryDialog from '../components/QuickTimeEntryDialog';
import { renderRichHtml } from '../utils/sanitizeHtml';
import { localISODate } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';

// Typo-Stufen der Seite: 24 px (Titel, Kennzahl) · 16 px (Abschnitt) · 14 px (Inhalt) · 12 px (Label)
// Raster: 24 px zwischen Abschnitten, 16 px Innenabstand in allen Kästen
const sectionSx = { border: '1px solid', borderColor: 'divider', overflow: 'hidden', mb: 3 };
const kpiValueSx = { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 };

const KpiLabel = ({ children }) => (
  <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
    {children}
  </Typography>
);

const CountBadge = ({ value, tone }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 20,
      height: 20,
      px: 0.75,
      borderRadius: 10,
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1,
      bgcolor: (theme) => (tone ? alpha(theme.palette[tone].main, 0.14) : theme.palette.grey[200]),
      color: (theme) => (tone ? theme.palette[tone].dark : theme.palette.text.secondary),
    }}
  >
    {value}
  </Box>
);

// Einheitlicher Kopf für alle Abschnitts-Kästen
const SectionHeader = ({ title, meta, count, countTone, aside, children }) => (
  <Box sx={{
    px: 2,
    py: 1,
    minHeight: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    flexWrap: 'wrap',
    borderBottom: '1px solid',
    borderColor: 'divider',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography component="h2" sx={{ fontSize: '1rem', fontWeight: 600 }}>{title}</Typography>
      {meta && <Typography variant="body2" color="text.secondary">{meta}</Typography>}
      {count > 0 && <CountBadge value={count} tone={countTone} />}
    </Box>
    {aside}
    {children && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{children}</Box>}
  </Box>
);

// Gemeinsames Spaltenraster für Posteingang und Wochenaufgaben,
// damit Kunde / Bearbeiter bzw. Datum / Status / Aktionen untereinander stehen
const TaskColGroup = () => (
  <colgroup>
    <col />
    <col style={{ width: '20%' }} />
    <col style={{ width: '18%' }} />
    <col style={{ width: 128 }} />
    <col style={{ width: 104 }} />
  </colgroup>
);

const taskTableSx = {
  tableLayout: 'fixed',
  minWidth: 760,
  '& .MuiTableCell-head': { fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', py: 1 },
  '& .MuiTableCell-body': { py: 0.75 },
};

// Aktions-Icon: Farbe trägt die Bedeutung, aber ohne gefüllten Kreis
const ActionButton = ({ title, color, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      aria-label={title}
      onClick={onClick}
      sx={{
        color: (theme) => alpha(theme.palette[color].main, 0.85),
        '&:hover': { color: `${color}.main`, bgcolor: (theme) => alpha(theme.palette[color].main, 0.08) },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

// Status-Chip: Statusfarbe als getönter Hintergrund statt Vollfläche
const StatusChip = ({ label, color }) => {
  let tint;
  try {
    const base = color || '#616161';
    tint = { bgcolor: alpha(base, 0.14), color: darken(base, 0.2), borderColor: alpha(base, 0.35) };
  } catch (e) {
    // Unbekanntes Farbformat: neutral darstellen statt abzustürzen
    tint = { bgcolor: 'grey.100', color: 'text.secondary', borderColor: 'divider' };
  }
  return (
    <Chip
      label={label}
      size="small"
      sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500, border: '1px solid', ...tint }}
    />
  );
};

const CommandCenterPage = () => {
  const { user, isAdmin: isAdminUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayHours: 0,
    weekHours: 0,
    weekDays: [],
  });
  const [billing, setBilling] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [quickTimeOpen, setQuickTimeOpen] = useState(false);
  const [myWorkData, setMyWorkData] = useState(null);
  const [inboxData, setInboxData] = useState({ tasks: [], count: 0, inbox_project: null });
  const [projects, setProjects] = useState([]);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [timeEntryData, setTimeEntryData] = useState({
    subtask_comment: '',
    hours: '',
    date: localISODate(),
  });
  const [editingTask, setEditingTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUserFilter, setSelectedUserFilter] = useState('all');
  const [isAdmin, setIsAdmin] = useState(false);
  const [quickInboxText, setQuickInboxText] = useState('');
  const [quickInboxLoading, setQuickInboxLoading] = useState(false);

  const handleQuickInbox = async () => {
    const name = quickInboxText.trim();
    if (!name) return;
    try {
      setQuickInboxLoading(true);
      await tasksAPI.quickInbox(name);
      setQuickInboxText('');
      // Refresh inbox
      const inboxResponse = await tasksAPI.getInbox();
      setInboxData(inboxResponse.data);
    } catch (err) {
      console.log('[QuickInbox] Error:', err);
    } finally {
      setQuickInboxLoading(false);
    }
  };

  // Toggle task description expansion
  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  useEffect(() => {
    loadCommandCenterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const EMPTY_MY_WORK = {
    overdue: [], today: [], this_week: [], later: [], no_due_date: [],
    counts: { overdue: 0, today: 0, this_week: 0, later: 0, no_due_date: 0, total: 0 },
  };

  const loadCommandCenterData = async () => {
    setLoading(true);
    const userIsAdmin = isAdminUser();
    setIsAdmin(userIsAdmin);

    // Zeiten aller Mitarbeiter (Admin sieht alle): heute und Woche ab Montag
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const today = localISODate(now);
    const weekDayDates = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return localISODate(d);
    });

    // Unabhängige Requests parallel; ein Fehler leert nur die betroffene Kachel
    const [week, projectsRes, myWork, inbox, usersRes] = await Promise.allSettled([
      timeEntriesAPI.getAll({ start_date: weekDayDates[0], end_date: today }),
      projectsAPI.getAll(),
      tasksAPI.getMyWork(userIsAdmin),
      tasksAPI.getInbox(),
      userIsAdmin ? usersAPI.getAll() : Promise.resolve({ data: [] }),
    ]);
    const failed = (name, r) => r.status === 'rejected' && console.error(`[DASHBOARD] ${name} nicht geladen:`, r.reason);

    failed('Zeiten', week);
    const weekEntries = week.status === 'fulfilled' ? week.value.data || [] : [];
    const sumFor = (date) => weekEntries
      .filter((e) => e.date === date)
      .reduce((sum, e) => sum + (e.hours || 0), 0);
    const weekDays = week.status === 'fulfilled'
      ? weekDayDates.map((date) => ({ date, hours: sumFor(date), future: date > today }))
      : [];
    setStats({ todayHours: sumFor(today), weekHours: weekDays.reduce((sum, d) => sum + d.hours, 0), weekDays });

    failed('Projekte', projectsRes);
    setProjects(projectsRes.status === 'fulfilled' ? projectsRes.value.data || [] : []);
    failed('Meine Aufgaben', myWork);
    setMyWorkData(myWork.status === 'fulfilled' ? myWork.value.data : EMPTY_MY_WORK);
    failed('Eingang', inbox);
    if (inbox.status === 'fulfilled') setInboxData(inbox.value.data);
    failed('Benutzer', usersRes);
    setUsers(usersRes.status === 'fulfilled' ? usersRes.value.data?.filter(u => u.is_active) || [] : []);

    if (userIsAdmin) {
      dashboardAPI.billing()
        .then((r) => setBilling(r.data))
        .catch((err) => console.log('[DASHBOARD] Abrechnungsdaten nicht verfügbar:', err));
    }
    customersAPI.getAll().then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    setLoading(false);
  };

  // Handle opening task for editing
  const handleEditTask = (task) => {
    setEditingTask(task);
    setEditDialogOpen(true);
  };

  // Handle task updated from edit dialog
  const handleTaskUpdated = () => {
    setEditDialogOpen(false);
    setEditingTask(null);
    loadCommandCenterData();
  };

  // Handle changing task status to "In Arbeit"
  const handleStartTask = async (task) => {
    try {
      // Find the "In Arbeit" status (order=1)
      const statusResponse = await api.get(`/task-statuses?project_id=${task.project_id}`);
      const inProgressStatus = statusResponse.data.find(s => s.order === 1);

      if (inProgressStatus) {
        await tasksAPI.update(task.id, { status_id: inProgressStatus.id });
        loadCommandCenterData(); // Reload data
      }
    } catch (error) {
      console.error('Error starting task:', error);
    }
  };

  // Handle reopening a completed task (set back to "In Bearbeitung")
  const handleReopenTask = async (task) => {
    try {
      // Find the "In Bearbeitung" status (order=1)
      const statusResponse = await api.get(`/task-statuses?project_id=${task.project_id}`);
      const inProgressStatus = statusResponse.data.find(s => s.order === 1);

      if (inProgressStatus) {
        await tasksAPI.update(task.id, { status_id: inProgressStatus.id });
        loadCommandCenterData(); // Reload data
      }
    } catch (error) {
      console.error('Error reopening task:', error);
    }
  };

  // Handle assigning inbox task to current user (sets due_date to today)
  const handleAssignToMe = async (task) => {
    try {
      const today = localISODate();
      // Assign to user and set due_date to today so it appears in "Heute" lane
      await tasksAPI.update(task.id, {
        assignee_id: user.id,
        due_date: today
      });
      loadCommandCenterData(); // Reload data
    } catch (error) {
      console.error('Error assigning task:', error);
    }
  };

  // Handle completing task - based on is_timetracker_only
  const handleCompleteTask = async (task) => {
    if (task.is_timetracker_only) {
      // Timetracker task: Open time entry dialog
      setSelectedTask(task);
      setTimeEntryData({
        subtask_comment: '',
        hours: '',
        date: localISODate(),
      });
      setCompleteDialogOpen(true);
    } else {
      // Non-timetracker task: Complete directly without dialog
      try {
        const statusResponse = await api.get(`/task-statuses?project_id=${task.project_id}`);
        const doneStatus = statusResponse.data.find(s => s.order === 2);

        if (doneStatus) {
          await tasksAPI.update(task.id, { status_id: doneStatus.id });
          loadCommandCenterData();
        }
      } catch (error) {
        console.error('Error completing task:', error);
      }
    }
  };

  const closeTimeEntryModal = () => {
    setCompleteDialogOpen(false);
    setSelectedTask(null);
    setTimeEntryData({
      subtask_comment: '',
      hours: '',
      date: localISODate(),
    });
  };

  const handleTimeEntryChange = (e) => {
    setTimeEntryData({
      ...timeEntryData,
      [e.target.name]: e.target.value,
    });
  };

  // Complete task with time entry
  const handleCompleteWithTime = async (e) => {
    if (e) e.preventDefault();
    if (!selectedTask || !timeEntryData.hours || !timeEntryData.subtask_comment) return;

    try {
      // Create time entry
      await timeEntriesAPI.create({
        task_id: selectedTask.id,
        subtask_comment: timeEntryData.subtask_comment,
        hours: parseFloat(timeEntryData.hours),
        date: timeEntryData.date,
        user_id: user.id,
      });

      // Find the "Erledigt" status (order=2)
      const statusResponse = await api.get(`/task-statuses?project_id=${selectedTask.project_id}`);
      const doneStatus = statusResponse.data.find(s => s.order === 2);

      if (doneStatus) {
        await tasksAPI.update(selectedTask.id, { status_id: doneStatus.id });
      }

      closeTimeEntryModal();
      loadCommandCenterData();
    } catch (error) {
      console.error('Error completing task with time:', error);
    }
  };

  // Check if task is "In Arbeit" (order=1)
  const isTaskInProgress = (task) => {
    return task.status?.order === 1;
  };

  // Get current week number
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Get end of current week (Sunday)
  const getEndOfWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + daysUntilSunday);
    endOfWeek.setHours(23, 59, 59, 999);
    return endOfWeek;
  };

  const currentWeekNumber = getWeekNumber(new Date());
  const endOfWeek = getEndOfWeek();

  // Group tasks by date for lane display (only this week)
  const groupTasksByDate = (tasks) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = localISODate(today);

    const groups = {
      overdue: [],
      today: [],
      completed: [],
      byDate: {} // { "2025-12-05": [...tasks], "2025-12-06": [...tasks] }
    };

    tasks.forEach(task => {
      // Completed tasks go to separate lane
      if (task._category === 'completed') {
        groups.completed.push(task);
        return;
      }

      if (!task.due_date) {
        // Tasks without due date go to a special "Ohne Datum" group
        if (!groups.byDate['no_date']) {
          groups.byDate['no_date'] = [];
        }
        groups.byDate['no_date'].push(task);
      } else {
        const dueDate = new Date(task.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const dueDateStr = task.due_date;

        // Only include tasks up to end of this week
        if (dueDate > endOfWeek) return;

        if (dueDate < today) {
          groups.overdue.push(task);
        } else if (dueDateStr === todayStr) {
          groups.today.push(task);
        } else {
          if (!groups.byDate[dueDateStr]) {
            groups.byDate[dueDateStr] = [];
          }
          groups.byDate[dueDateStr].push(task);
        }
      }
    });

    // Sort byDate keys
    const sortedDates = Object.keys(groups.byDate)
      .filter(d => d !== 'no_date')
      .sort((a, b) => new Date(a) - new Date(b));

    // Add 'no_date' at the end if exists
    if (groups.byDate['no_date']) {
      sortedDates.push('no_date');
    }

    return { ...groups, sortedDates };
  };

  const formatDateLabel = (dateStr) => {
    if (dateStr === 'no_date') return 'Ohne Datum';
    const date = new Date(dateStr);
    const options = { weekday: 'long', day: '2-digit', month: '2-digit' };
    return date.toLocaleDateString('de-DE', options);
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Calculate week tasks (overdue + today + this_week + completed)
  const allWeekTasks = [
    ...(myWorkData?.overdue || []).map(task => ({ ...task, _category: 'overdue' })),
    ...(myWorkData?.today || []).map(task => ({ ...task, _category: 'today' })),
    ...(myWorkData?.this_week || []).map(task => ({ ...task, _category: 'this_week' })),
    ...(myWorkData?.completed || []).map(task => ({ ...task, _category: 'completed' })),
  ];

  // Filter by selected user (admin only)
  const weekTasks = selectedUserFilter === 'all'
    ? allWeekTasks
    : allWeekTasks.filter(task => task.assignee_id === parseInt(selectedUserFilter));

  const tileSx = (accent) => ({
    p: 2,
    border: '1px solid',
    borderColor: 'divider',
    borderTop: '2px solid',
    borderTopColor: (theme) => alpha(theme.palette[accent].main, 0.55),
  });

  // Aufklapp-Chevron steht hinter dem Namen, damit alle Tasknamen links fluchten
  const renderTaskName = (task) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Typography variant="body2" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
        {task.name}
      </Typography>
      {task.description && (
        <IconButton
          size="small"
          aria-label={expandedTasks[task.id] ? 'Beschreibung ausblenden' : 'Beschreibung anzeigen'}
          onClick={(e) => { e.stopPropagation(); toggleTaskExpansion(task.id); }}
          sx={{ p: 0.25, color: 'text.secondary' }}
        >
          {expandedTasks[task.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      )}
    </Box>
  );

  const renderDescriptionRow = (task) => task.description && (
    <TableRow>
      <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedTasks[task.id] ? 1 : 0, borderColor: 'divider' }}>
        <Collapse in={expandedTasks[task.id]} timeout="auto" unmountOnExit>
          <Box sx={{ py: 1.5, px: 2, mx: -2, bgcolor: 'grey.50' }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                wordBreak: 'break-word',
                lineHeight: 1.6,
                '& p': { my: 0.5 },
                '& ul, & ol': { pl: 3, my: 0.5 },
                '& li': { mb: 0.25 },
                '& a': { color: 'primary.main' },
              }}
              dangerouslySetInnerHTML={{
                __html: renderRichHtml(task.description)
              }}
            />
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );

  const renderWeekActions = (task) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <ActionButton title="Task bearbeiten" color="primary" onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}>
        <EditIcon fontSize="small" />
      </ActionButton>
      {isTaskInProgress(task) ? (
        <ActionButton title="Als erledigt markieren" color="success" onClick={(e) => { e.stopPropagation(); handleCompleteTask(task); }}>
          <DoneIcon fontSize="small" />
        </ActionButton>
      ) : (
        <ActionButton title="In Bearbeitung setzen" color="warning" onClick={(e) => { e.stopPropagation(); handleStartTask(task); }}>
          <StartIcon fontSize="small" />
        </ActionButton>
      )}
    </Box>
  );

  const renderReopenAction = (task) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <ActionButton title="Wieder öffnen" color="info" onClick={(e) => { e.stopPropagation(); handleReopenTask(task); }}>
        <ReopenIcon fontSize="small" />
      </ActionButton>
    </Box>
  );

  const renderWeekRow = (task, renderActions, muted) => (
    <React.Fragment key={task.id}>
      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' }, ...(muted && { opacity: 0.6 }) }}>
        <TableCell>{renderTaskName(task)}</TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" noWrap>
            {task.customer_name || '–'}
          </Typography>
        </TableCell>
        <TableCell>
          {task.assignee ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Avatar sx={{ width: 22, height: 22, fontSize: '0.75rem', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }}>
                {(task.assignee.full_name || task.assignee.username || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="body2" color="text.secondary" noWrap>
                {task.assignee.full_name || task.assignee.username}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">–</Typography>
          )}
        </TableCell>
        <TableCell>
          {task.status?.name && <StatusChip label={task.status.name} color={task.status.color} />}
        </TableCell>
        <TableCell align="right">{renderActions(task)}</TableCell>
      </TableRow>
      {renderDescriptionRow(task)}
    </React.Fragment>
  );

  // Gruppenzeile innerhalb der Wochentabelle (Überfällig, Heute, Datum, Erledigt)
  const renderLane = (key, label, tasks, { tone, renderActions = renderWeekActions, muted = false } = {}) => (
    <React.Fragment key={key}>
      <TableRow>
        <TableCell
          colSpan={5}
          sx={{
            py: 1,
            bgcolor: (theme) => (tone ? alpha(theme.palette[tone].main, 0.06) : theme.palette.grey[50]),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: tone === 'error' ? 'error.main' : muted ? 'text.secondary' : 'text.primary' }}
            >
              {label}
            </Typography>
            <CountBadge value={tasks.length} tone={muted ? undefined : tone} />
          </Box>
        </TableCell>
      </TableRow>
      {tasks.map((task) => renderWeekRow(task, renderActions, muted))}
    </React.Fragment>
  );

  const groupedTasks = groupTasksByDate(weekTasks);
  const hasAnyTasks = groupedTasks.overdue.length > 0 ||
                     groupedTasks.today.length > 0 ||
                     groupedTasks.completed.length > 0 ||
                     groupedTasks.sortedDates.some(d => groupedTasks.byDate[d]?.length > 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Kopfzeile mit den beiden Direkteinstiegen */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.25, color: 'primary.main' }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateTaskOpen(true)}>
            Task
          </Button>
          <Button variant="contained" startIcon={<TimeIcon />} onClick={() => setQuickTimeOpen(true)}>
            Zeit erfassen
          </Button>
        </Box>
      </Box>

      {/* Kennzahlen: Zeit, Abrechnung Vormonat, offene Rechnungen.
          Gleiches Gerüst in jeder Kachel: Label, Wert, Erläuterung */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: isAdmin ? 'repeat(3, 1fr)' : '1fr' }, gap: 3, mb: 3 }}>
        <Paper elevation={0} sx={tileSx('primary')}>
          <KpiLabel>{isAdmin ? 'Erfasste Zeit (Team)' : 'Erfasste Zeit'}</KpiLabel>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography sx={{ ...kpiValueSx, color: 'primary.main' }}>{formatDuration(stats.todayHours)}</Typography>
              <Typography variant="body2" color="text.secondary">heute</Typography>
            </Box>
            <Box>
              <Typography sx={kpiValueSx}>{formatDuration(stats.weekHours)}</Typography>
              <Typography variant="body2" color="text.secondary">diese Woche</Typography>
            </Box>
          </Box>
          {/* Wochenbalken Mo–So: Tage ohne Buchung fallen sofort auf */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mt: 2 }}>
            {stats.weekDays.map((d, i) => {
              const max = Math.max(8, ...stats.weekDays.map((x) => x.hours));
              const isToday = !d.future && (i === stats.weekDays.length - 1 || stats.weekDays[i + 1].future);
              return (
                <Tooltip key={d.date} title={`${['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][i]}: ${formatDuration(d.hours)}`}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ height: 32, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                      <Box sx={{
                        width: '100%',
                        maxWidth: 28,
                        height: Math.max(3, (d.hours / max) * 32),
                        borderRadius: 0.5,
                        bgcolor: (theme) => (d.future
                          ? theme.palette.grey[100]
                          : d.hours > 0
                            ? alpha(theme.palette.primary.main, isToday ? 0.9 : 0.55)
                            : theme.palette.grey[300]),
                      }} />
                    </Box>
                    <Typography variant="caption" sx={{ lineHeight: 1, color: isToday ? 'primary.main' : 'text.secondary', fontWeight: isToday ? 600 : 400 }}>
                      {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'][i]}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Paper>

        {isAdmin && (
          <Paper elevation={0} sx={tileSx(billing?.backlog.open.length ? 'secondary' : 'success')}>
            <KpiLabel>Abrechnung {billing?.backlog.month_label || 'Vormonat'}</KpiLabel>
            {!billing ? (
              <Typography variant="body2" color="text.secondary">…</Typography>
            ) : billing.backlog.open.length === 0 ? (
              <Typography sx={{ ...kpiValueSx, color: 'success.main' }}>
                {billing.backlog.customers_with_time ? 'Alles abgerechnet ✓' : 'Keine Zeiten erfasst'}
              </Typography>
            ) : (
              <>
                <Typography sx={{ ...kpiValueSx, color: 'secondary.main' }}>
                  {billing.backlog.open.length} {billing.backlog.open.length === 1 ? 'Kunde' : 'Kunden'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>noch ohne Rechnung</Typography>
                {billing.backlog.open.slice(0, 3).map((c) => (
                  <Box
                    key={c.customer_id}
                    onClick={() => navigate('/reports', { state: { prefill: {
                      customer_id: c.customer_id,
                      start_date: billing.backlog.date_from,
                      end_date: billing.backlog.date_to,
                      label: `${c.customer_name}, ${billing.backlog.month_label}`,
                    } } })}
                    sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 1, px: 0.5, mx: -0.5, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Typography variant="body2" noWrap sx={{ mr: 1 }}>{c.customer_name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{formatDuration(c.hours)}</Typography>
                  </Box>
                ))}
                {billing.backlog.open.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    und {billing.backlog.open.length - 3} weitere
                  </Typography>
                )}
              </>
            )}
          </Paper>
        )}

        {isAdmin && (
          <Paper
            elevation={0}
            onClick={() => navigate('/invoices', { state: { statusTab: 2 } })}
            sx={{ ...tileSx(billing?.open_invoices.overdue_count ? 'error' : 'info'), cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <KpiLabel>Offene Rechnungen</KpiLabel>
            {!billing ? (
              <Typography variant="body2" color="text.secondary">…</Typography>
            ) : billing.open_invoices.count === 0 ? (
              <Typography sx={{ ...kpiValueSx, color: 'success.main' }}>Alles bezahlt ✓</Typography>
            ) : (
              <>
                <Typography sx={kpiValueSx}>
                  {billing.open_invoices.total_gross.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {billing.open_invoices.count} {billing.open_invoices.count === 1 ? 'Rechnung' : 'Rechnungen'} versendet
                </Typography>
                {billing.open_invoices.overdue_count > 0 && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                    {billing.open_invoices.overdue_count} überfällig, älteste seit {billing.open_invoices.oldest_overdue_days} Tagen
                  </Typography>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>

      {/* Posteingang: Schnelleingabe + nicht zugewiesene Tasks aus E-Mail-Import */}
      <Paper elevation={0} sx={sectionSx}>
        <SectionHeader
          title="Posteingang"
          count={inboxData.count}
          countTone="info"
          aside={<Typography variant="caption" color="text.secondary">Nicht zugewiesene Tasks aus E-Mail-Import</Typography>}
        />
        <Box sx={{ px: 2, py: 1.5, borderBottom: inboxData.count > 0 ? '1px solid' : 0, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Task zum Posteingang hinzufügen …"
            value={quickInboxText}
            onChange={(e) => setQuickInboxText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleQuickInbox(); }}
            disabled={quickInboxLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: quickInboxText.trim() && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleQuickInbox}
                    disabled={quickInboxLoading}
                    sx={{ color: 'primary.main' }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {inboxData.count > 0 && (
          <TableContainer>
            <Table size="small" sx={taskTableSx}>
              <TaskColGroup />
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Kunde</TableCell>
                  <TableCell>Erstellt am</TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {inboxData.tasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>{renderTaskName(task)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {task.customer_name || '–'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(task.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <ActionButton title="Mir zuweisen" color="success" onClick={() => handleAssignToMe(task)}>
                            <AssignIcon fontSize="small" />
                          </ActionButton>
                          <ActionButton title="Task bearbeiten" color="primary" onClick={() => handleEditTask(task)}>
                            <EditIcon fontSize="small" />
                          </ActionButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {renderDescriptionRow(task)}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Aufgaben dieser Woche: eine Tabelle, Lanes als Gruppenzeilen */}
      <Paper elevation={0} sx={sectionSx}>
        <SectionHeader title="Aufgaben in dieser Woche" meta={`KW ${currentWeekNumber}`}>
          {isAdmin && (
            <TextField
              select
              size="small"
              value={selectedUserFilter}
              onChange={(e) => setSelectedUserFilter(e.target.value)}
              sx={{ minWidth: 180, '& .MuiSelect-select': { py: 0.75, fontSize: '0.875rem' } }}
            >
              <MenuItem value="all">Alle Mitarbeiter</MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.full_name || user.username}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button size="small" onClick={() => navigate('/my-work')} sx={{ px: 1.5, py: 0.75 }}>
            Alle anzeigen
          </Button>
        </SectionHeader>

        {!hasAnyTasks ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CompleteIcon sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Alles erledigt!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keine offenen Tasks
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={taskTableSx}>
              <TaskColGroup />
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Kunde</TableCell>
                  <TableCell>Bearbeiter</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedTasks.overdue.length > 0 &&
                  renderLane('overdue', 'Überfällig', groupedTasks.overdue, { tone: 'error' })}
                {groupedTasks.today.length > 0 &&
                  renderLane('today', 'Heute', groupedTasks.today, { tone: 'warning' })}
                {groupedTasks.sortedDates.map((dateStr) => {
                  const tasks = groupedTasks.byDate[dateStr];
                  if (!tasks || tasks.length === 0) return null;
                  return renderLane(dateStr, formatDateLabel(dateStr), tasks);
                })}
                {groupedTasks.completed.length > 0 &&
                  renderLane('completed', 'Erledigt diese Woche', groupedTasks.completed, { renderActions: renderReopenAction, muted: true })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Time Entry Modal */}
      <Dialog
        open={completeDialogOpen}
        onClose={closeTimeEntryModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Zeiteintrag erstellen
          </Typography>
          <IconButton onClick={closeTimeEntryModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="time-entry-form" onSubmit={handleCompleteWithTime}>
            <TextField
              select
              fullWidth
              margin="normal"
              label="Projekt"
              value={selectedTask?.project_id || ''}
              disabled
            >
              <MenuItem value="">Projekt auswählen</MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              margin="normal"
              label="Task"
              value={selectedTask?.name || ''}
              disabled
            />

            <TextField
              fullWidth
              margin="normal"
              label="Subtask Kommentar"
              name="subtask_comment"
              value={timeEntryData.subtask_comment}
              onChange={handleTimeEntryChange}
              multiline
              rows={3}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Stunden"
              name="hours"
              type="number"
              value={timeEntryData.hours}
              onChange={handleTimeEntryChange}
              inputProps={{ step: 0.25, min: 0.25 }}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Datum"
              name="date"
              type="date"
              value={timeEntryData.date}
              onChange={handleTimeEntryChange}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeTimeEntryModal}
            sx={{
              color: '#1e3a5f',
              '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.08)' },
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="time-entry-form"
            variant="contained"
            sx={{
              bgcolor: '#e01e26',
              '&:hover': { bgcolor: '#c41920' },
            }}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Edit Dialog */}
      {editDialogOpen && editingTask && (
        <TaskEditDialog
          key={`task-edit-${editingTask?.id}`}
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingTask(null);
          }}
          task={editingTask}
          projectId={editingTask?.project_id}
          onTaskUpdated={handleTaskUpdated}
        />
      )}
      {createTaskOpen && (
        <TaskCreateDialog
          open={createTaskOpen}
          onClose={() => setCreateTaskOpen(false)}
          projects={projects}
          allProjects={projects}
          customers={customers}
          onTaskCreated={() => { setCreateTaskOpen(false); loadCommandCenterData(); }}
        />
      )}
      <QuickTimeEntryDialog
        open={quickTimeOpen}
        onClose={() => setQuickTimeOpen(false)}
        onSaved={loadCommandCenterData}
      />
    </Container>
  );
};

export default CommandCenterPage;
